/**
 * Billing Hooks - TanStack Query implementation
 *
 * Server state management for billing and revenue data.
 * Fixed to use correct API method names from api-client
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { queryKeys, invalidateQueries } from '@/lib/query-keys'
import { toast } from 'sonner'
import type { InvoiceStatus } from '@/lib/types'

// Types
export interface InvoiceParams {
  status?: string
  tenantId?: number
  month?: string
  page?: number
}

export interface CreateInvoiceData {
  tenantId: number
  month: string
  amountCents: number
  currency?: string
  dueDate?: string
}

export interface UpdateInvoiceStatusData {
  status: InvoiceStatus
  paidAt?: string
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Fetch all invoices with optional filters
 */
export function useInvoices(params: InvoiceParams = {}) {
  return useQuery<any[]>({
    queryKey: [...queryKeys.billing.invoices(), params] as const,
    queryFn: async () => {
      const response = await api.listAllInvoices(params)
      return response.data || []
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

/**
 * Fetch invoice detail by ID
 */
export function useInvoice(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.billing.detail(id) : ['invoice', 'null'],
    queryFn: async () => {
      if (!id) return null
      const response = await api.getInvoice(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch revenue stats for a specific month
 */
export function useRevenueStats(month?: string) {
  return useQuery({
    queryKey: [...queryKeys.billing.revenue(), month] as const,
    queryFn: async () => {
      const response = await api.getRevenueStats(month || new Date().toISOString().slice(0, 7))
      return response.data
    },
    enabled: !!month,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}

/**
 * Fetch revenue share breakdown
 */
export function useRevenueShares(params?: { month?: string; status?: string; limit?: number }) {
  return useQuery({
    queryKey: [...queryKeys.billing.shares(), params] as const,
    queryFn: async () => {
      const response = await api.getRevenueShares(params)
      return response.data
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create new invoice
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateInvoiceData) => {
      const response = await api.createInvoice(data)
      if (!response.success) throw new Error(response.error || 'Failed to create invoice')
      return response.data
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'billing')
      toast.success('Invoice created')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create invoice')
    },
  })
}

/**
 * Update invoice status
 */
export function useUpdateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, status, paidAt }: { id: number; status: InvoiceStatus; paidAt?: string }) => {
      const response = await api.updateInvoiceStatus(id, status, paidAt)
      if (!response.success) throw new Error(response.error || 'Failed to update invoice')
      return response.data
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'billing')
      toast.success('Invoice updated')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update invoice')
    },
  })
}

/**
 * Mark invoice as paid
 */
export function useMarkInvoicePaid() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, paidAt }: { id: number; paidAt?: string }) => {
      const response = await api.markInvoicePaid(id, paidAt)
      if (!response.success) throw new Error(response.error || 'Failed to update invoice')
      return response.data
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'billing')
      toast.success('Invoice marked as paid')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update invoice')
    },
  })
}

/**
 * Send invoice reminder
 */
export function useSendInvoiceReminder() {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.markInvoiceSent(id)
      if (!response.success) throw new Error(response.error || 'Failed to send reminder')
      return response.data
    },

    onSuccess: () => {
      toast.success('Reminder sent')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to send reminder')
    },
  })
}

/**
 * Generate monthly invoices for all tenants
 */
export function useGenerateMonthlyInvoices() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (month: string) => {
      const response = await api.generateInvoices(month)
      if (!response.success) throw new Error(response.error || 'Failed to generate invoices')
      return response.data
    },

    onSuccess: () => {
      invalidateQueries(queryClient, 'billing')
      toast.success('Monthly invoices generated')
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to generate invoices')
    },
  })
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook to refetch all billing queries
 */
export function useRefetchBilling() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.billing.root,
    })
  }
}
